import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";
import { NavigationEnd, Router } from "@angular/router";
import { Observable } from "rxjs";
import { filter, map, startWith } from "rxjs/operators";

// Models
import { ContextLocationViewModel } from "src/app/_core/models/main/context-location.model";
import { LinkItem } from "src/app/_core/models/main/link-item.model";
import { PlaceViewModel } from "src/app/_core/models/main/place.model";
import { UserProfileViewModel } from "src/app/_core/models/main/user-profile.model";

// Services
//import { SnackBarService } from './../../globalServices/snackBar.service';
import { TranslateService } from "@ngx-translate/core";
import { FavPageViewModel } from "src/app/_core/models/home/fav-page.model";
import { AuthService } from "src/app/_core/providers/auth.service";
import {
	GlobalService,
	MigaDePan,
} from "src/app/_core/providers/global.service";

// Constants
import { HOME_MENU_ITEMS } from "src/app/_core/constants/menu.constants";
import { HeaderService } from "src/app/_core/providers/header.service";
import { UserProfileStore } from "src/app/_core/stores/user-profile.store";

// Material
import { MatDialog } from "@angular/material/dialog";
import { CommandPaletteService } from "src/app/_core/providers/command-palette.service";
import { GlobalSearchService } from "src/app/_core/providers/global-search.service";

// Components
import { Policies } from "src/app/_core/models/main/policies.enum";
import { ApplicationViewModel } from "src/app/_core/models/master/application.model";
import { DialogService } from "src/app/_core/services/dialog.service";

@Component({
    selector: "app-header",
    templateUrl: "./header.component.html",
    styleUrls: ["./header.component.scss"],
    standalone: false
})
export class HeaderComponent implements OnInit {
	@Input() favPage!: FavPageViewModel;
	@Output() unfavEvent = new EventEmitter<string>();
	@Output() toggleProfile: EventEmitter<any> = new EventEmitter();

	// Constructor
	constructor(
		private readonly _router: Router,
		private readonly _headerService: HeaderService,
		private readonly _authService: AuthService,
		private readonly _userProfileStore: UserProfileStore,
		private readonly _translateService: TranslateService,
		private readonly _global: GlobalService,
		public dialog: MatDialog,
		private readonly _commandPalette: CommandPaletteService,
		private readonly _globalSearch: GlobalSearchService,
		private readonly _dialogService: DialogService
	) {
		//   _router.events.subscribe(value => {
		//     this.currentUrl = this._router.url.toString();
		//     this.refreshIsFav();
		// });

		_router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((_event: NavigationEnd) => {
				this.currentFilter = "";
				this.currentUrl = this._router.url.toString();
				this.refreshIsFav();
			});

		this.userProfile = this._userProfileStore.getUserProfile();
	}

	// Properties
	public migas: MigaDePan[] = [];
	public breadcrumbs: LinkItem[];
	public current: LinkItem;
	public HOME_MENU_ITEMS = HOME_MENU_ITEMS;
	public toolbar: ApplicationViewModel[];
	public stateCtrl = new FormControl();
	public filteredPlaces: Observable<PlaceViewModel[]>;
	public places: PlaceViewModel[];
	public sectors: PlaceViewModel[];
	public place: PlaceViewModel;
	public contextLocation: ContextLocationViewModel;
	public currentFilter: string;
	public userProfile: UserProfileViewModel;
	public isFavPage: boolean;
	public currentUrl: string;
	public favPageList: FavPageViewModel[];
	public displayDialog: boolean;
	public isMenuCollapsed = true;
	public darkMode = false;
	public photo: string | null = null;
	/** Etiqueta del atajo según el SO: "⌘K" en Apple, "Ctrl+K" en el resto. */
	public readonly shortcutLabel: string = this.detectAppleOs() ? "⌘K" : "Ctrl+K";

	/**
	 * Marca activo el item de menú de la sección actual, incluyendo rutas de
	 * detalle que no comparten path con el listado (p. ej. /centro/:id → Centros,
	 * /cebe/:id → Contratos). Sustituye a routerLinkActive (que no cubría esos casos).
	 */
	isLinkActive(link: { url?: string | null }): boolean {
		if (!link?.url) {
			return false;
		}
		const url = (this.currentUrl || "")
			.split("?")[0]
			.split("#")[0]
			.toLowerCase();
		const base = link.url.toLowerCase();
		if (url === base || url.startsWith(base + "/")) {
			return true;
		}
		// Alias de sección: el detalle de centro pertenece a "Centros".
		if (
			base === "/sites" &&
			(url === "/sites" || url.startsWith("/sites"))
		) {
			return true;
		}
		// El mapa global, "Mi perfil" y "Mis responsabilidades" cuelgan de "Inicio"
		// (funciones de nivel principal).
		if (
			base === "/home" &&
			(url.startsWith("/map") ||
				url.startsWith("/my-profile") ||
				url.startsWith("/my-responsibilities"))
		) {
			return true;
		}
		// Alias: detalle de CeBe pertenece a "Contratos".
		if (base === "/contracts" && (url === "/cebe" || url.startsWith("/cebe/"))) {
			return true;
		}
		return false;
	}

	// Events
	ngOnInit() {
		this._global.migas$.subscribe((migas) => {
			this.migas = migas;
		});
		this.currentUrl = this._router.url.toString();
		this.getData();
		this._loadUserPhoto();
		this._refillFromGlobal();

		// Precarga del índice del Command Palette en segundo plano (no bloquea).
		this._globalSearch.prefetch();

		this._global.changesDetected.subscribe((_result) => {
			this._refillFromGlobal();
		});
	}

	// Foto de perfil (EntraID / Azure AD) recuperada dinámicamente
	private _loadUserPhoto(): void {
		const email = this.userProfile?.username;
		if (!email) {
			return;
		}
		this._authService.getUserPhoto(email).subscribe({
			next: (blob) => {
				if (!blob) {
					return;
				}
				const reader = new FileReader();
				reader.onload = () => {
					this.photo = reader.result as string;
				};
				reader.readAsDataURL(blob);
			},
			error: () => {
				this.photo = null;
			},
		});
	}

	// Public methods
	onSearch(event: any) {
		this.currentFilter = event;
	}

	/** Abre el Command Palette (Ctrl/Cmd+K) desde la lupa del header. */
	openCommandPalette(): void {
		this._commandPalette.open();
	}

	/** Detecta macOS/iOS para mostrar ⌘ en lugar de Ctrl. */
	private detectAppleOs(): boolean {
		const nav = window.navigator as Navigator & {
			userAgentData?: { platform?: string };
		};
		const platform = (
			nav.userAgentData?.platform ??
			nav.platform ??
			""
		).toLowerCase();
		return (
			platform.includes("mac") ||
			platform.includes("iphone") ||
			platform.includes("ipad")
		);
	}

	placeSelected(event: any) {
		try {
			this._router.navigate([event.option.value]).then(() => {
				window.location.reload();
			});
		} catch (error) {
			console.error("Navigation error:", error);
		}
	}

	refreshIsFav() {
		this.isFavPage = false;
		if (
			this.favPageList &&
			this.favPageList.length > 0 &&
			this.currentUrl
		) {
			const aux = this.favPageList.filter((x) =>
				x.url == null
					? ""
					: x.url.toLowerCase() == this.currentUrl.toLowerCase()
			);
			this.isFavPage = aux && aux.length > 0;
		}
	}

	savePageFav() {
		const auxName = this.HOME_MENU_ITEMS.filter(
			(x) =>
				x.url === this.currentUrl.replace(this.current?.url || "", "")
		);
		const stringName =
			auxName && auxName.length > 0
				? this._translateService.instant(auxName[0].text)
				: this._translateService.instant(
						"header.toolbar.favorites.dashboard"
				  );
		// Call the back-end
	}

	deletePageFav() {
		const auxName = this.HOME_MENU_ITEMS.filter(
			(x) =>
				x.url === this.currentUrl.replace(this.current?.url || "", "")
		);
		const stringName =
			auxName && auxName.length > 0
				? this._translateService.instant(auxName[0].text)
				: this._translateService.instant(
						"header.toolbar.favorites.dashboard"
				  );

		// Call the back-end
	}

	displayFn(place: PlaceViewModel): string {
		return place ? place.name : "";
	}

	emitProfile() {
		this.toggleProfile.emit(null);
	}

	openLogOutDialog() {
		this._dialogService
			.confirm({
				type: "confirm",
				icon: "logout",
				title: this._translateService.instant("header.toolbar.logout"),
				message: this._translateService.instant(
					"shared.confirmDialog.logoutMessage",
				),
				confirmText: this._translateService.instant("header.toolbar.logout"),
				cancelText: this._translateService.instant("common.cancel"),
			})
			.subscribe((confirmed) => {
				if (confirmed) {
					this._logOut();
				}
			});
	}

	collapseMenu() {
		this.isMenuCollapsed = true;
	}

	getData() {
		this._headerService.getPlaces().subscribe((result) => {
			this.places = result.sort((a, b) => (a.name > b.name ? 1 : -1));
			this._calculateData();
		});

		// Call back-end to get favs
	}

	hasAccess(link: LinkItem): boolean {
		const policies = link.policies;
		let result = false;

		if (!policies || (policies && policies.length == 0)) {
			result = true;
		} else {
			const userProfile = this._userProfileStore.getUserProfile();
			const userPolicies = userProfile.profiles.map((up) => up.profile);

			if (userPolicies.indexOf(Policies.Admin) > -1) {
				result = true;
			} else if (
				userProfile &&
				userProfile.profiles &&
				userProfile.profiles.length > 0
			) {
				const violatedPolicies = policies.filter(
					(p) => userPolicies.indexOf(p) == -1
				);
				result = violatedPolicies && violatedPolicies.length == 0;
			}
		}

		return result;
	}

	public enableBreadcrumbLink(link: LinkItem, isLast: boolean): boolean {
		return link.active === true && !isLast && this.hasAccess(link);
	}

	// Private methods
	// private _replaceAll(str, find, replace) {
	//     var escapedFind = find.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, "\\$1");
	//     return str.replace(new RegExp(escapedFind, 'g'), replace);
	// }

	private _refillFromGlobal() {
		this.breadcrumbs = this._global.breadcrumbs;
		this.current = this._global.current;
		this.toolbar = this._global.toolbar;
		//this._setupPlants();
	}

	private _calculateData() {
		this.filteredPlaces = this.stateCtrl.valueChanges.pipe(
			startWith(""),
			map((state) =>
				state ? this._filterStates(state) : this.places.slice()
			)
		);
	}

	private _filterStates(value: string): PlaceViewModel[] {
		const filterValue = value.toLowerCase();
		return this.places.filter(
			(x) =>
				x.name.toLowerCase().indexOf(filterValue) >= 0 ||
				x.id?.toString().toLowerCase().indexOf(filterValue) >= 0 ||
				x.code?.toLowerCase().indexOf(filterValue) >= 0 ||
				x.typeNameTranslation.toLowerCase().indexOf(filterValue) >= 0
		);
	}

	/*   private _setupPlants() {
          this.contextLocation = this._global.lastPlace;
          let auxSectors = this.places ? this.places.filter(x => x.parentId && x.parentId.toLowerCase() == (this._global.lastPlace?.cebe?() ?? "NO BUSCAR")) : [];
          this.sectors = this._setupSectorsRecursive(auxSectors, null, 0)
      } */

	// private _setupSectorsRecursive(sectors: PlaceViewModel[], parent: string, level: number): PlaceViewModel[] {
	//     let result: PlaceViewModel[] = [];
	//     let auxSectors = sectors.filter(x => (!parent && !x.internalParentId) || (x.internalParentId && x.internalParentId.toLowerCase() == parent)).sort((x, y) => x.parentId > y.parentId ? 1 : -1);
	//     for (var auxSector of auxSectors) {
	//         auxSector.level = level;
	//         result.push(auxSector);
	//         result = result.concat(this._setupSectorsRecursive(sectors, auxSector.id, level + 1));
	//     }
	//     return result;
	// }

	private _logOut() {
		this._authService.logOut();
		this._router.navigate(["/"]);
	}
}
